import { useState, useEffect, useRef } from 'react';
import { productsApi } from '../services/crmApi';
import type { ProductScoringConfig } from '../../../shared/scoringTypes';

const cache = new Map<string, ProductScoringConfig | null>();

export function useProductScoring(serviceType?: string) {
  const [scoringConfig, setScoringConfig] = useState<ProductScoringConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!serviceType) {
      setScoringConfig(null);
      return;
    }

    if (cache.has(serviceType)) {
      setScoringConfig(cache.get(serviceType)!);
      return;
    }

    setLoading(true);
    productsApi.getScoringConfig(serviceType)
      .then(data => {
        if (!mountedRef.current) return;
        const config = data.config || null;
        cache.set(serviceType, config);
        setScoringConfig(config);
      })
      .catch(() => {
        if (!mountedRef.current) return;
        cache.set(serviceType, null);
        setScoringConfig(null);
      })
      .finally(() => {
        if (mountedRef.current) setLoading(false);
      });
  }, [serviceType]);

  const hasScoringEnabled = scoringConfig?.enabled === true;
  const minPoints = scoringConfig?.minPoints ?? null;

  return { scoringConfig, loading, hasScoringEnabled, minPoints };
}

export function invalidateScoringCache(serviceType?: string) {
  if (serviceType) {
    cache.delete(serviceType);
  } else {
    cache.clear();
  }
}
